/**
 * @file Loading state component using Bootstrap 5.3
 * @version 1.0.0
 * @author Matthieu Chareton
 * @since 2025-07-08
 */

const { defineComponent, h } = Vue;

export default defineComponent({
  name: 'LoadingState',

  props: {
    variant: {
      type: String,
      default: 'spinner',
      validator: value =>
        ['spinner', 'skeleton', 'placeholder', 'progress'].includes(value),
    },
    size: {
      type: String,
      default: 'normal',
      validator: value => ['small', 'normal', 'large'].includes(value),
    },
    color: {
      type: String,
      default: 'primary',
      validator: value =>
        [
          'primary',
          'secondary',
          'success',
          'danger',
          'warning',
          'info',
          'light',
          'dark',
        ].includes(value),
    },
    message: {
      type: String,
      default: 'Loading...',
    },
    inline: {
      type: Boolean,
      default: false,
    },
    overlay: {
      type: Boolean,
      default: false,
    },
    rows: {
      type: Number,
      default: 3,
    },
  },

  setup(props) {
    const getSpinnerSize = () => {
      const sizes = {
        small: 'spinner-border-sm',
        normal: '',
        large: 'fs-3',
      };
      return sizes[props.size];
    };

    const renderSpinner = () => {
      return h(
        'div',
        {
          class: `spinner-border text-${props.color} ${getSpinnerSize()}`,
          role: 'status',
        },
        [h('span', { class: 'visually-hidden' }, props.message)]
      );
    };

    const renderSkeleton = () => {
      const rows = [];
      for (let i = 0; i < props.rows; i++) {
        const width = i === props.rows - 1 ? '75%' : '100%';
        rows.push(
          h(
            'div',
            {
              key: i,
              class: 'placeholder-glow mb-2',
            },
            [
              h('span', {
                class: `placeholder col-${i === props.rows - 1 ? '9' : '12'} bg-${props.color}`,
                style: { opacity: 0.5 },
              }),
            ]
          )
        );
      }
      return h('div', { class: 'skeleton-loader' }, rows);
    };

    const renderPlaceholder = () => {
      return h(
        'div',
        {
          class: `placeholder-glow text-${props.color}`,
        },
        [
          h('span', { class: 'placeholder col-7' }),
          h('span', { class: 'placeholder col-4' }),
          h('span', { class: 'placeholder col-4' }),
          h('span', { class: 'placeholder col-6' }),
          h('span', { class: 'placeholder col-8' }),
        ]
      );
    };

    const renderProgress = () => {
      return h(
        'div',
        {
          class: `progress ${props.size === 'small' ? 'progress-sm' : ''}`,
        },
        [
          h('div', {
            class: `progress-bar progress-bar-striped progress-bar-animated bg-${props.color}`,
            role: 'progressbar',
            style: { width: '100%' },
          }),
        ]
      );
    };

    const renderContent = () => {
      const variants = {
        spinner: renderSpinner,
        skeleton: renderSkeleton,
        placeholder: renderPlaceholder,
        progress: renderProgress,
      };

      const content = variants[props.variant]();

      if (
        props.variant === 'spinner' &&
        props.message &&
        props.size !== 'small'
      ) {
        return h('div', { class: 'text-center' }, [
          content,
          h('div', { class: `mt-2 text-${props.color}` }, props.message),
        ]);
      }

      return content;
    };

    return () => {
      const content = renderContent();

      if (props.overlay) {
        return h(
          'div',
          {
            class:
              'd-flex justify-content-center align-items-center position-absolute top-0 start-0 w-100 h-100',
            style: {
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 1000,
            },
          },
          [h('div', { class: 'text-center' }, [content])]
        );
      }

      if (props.inline) {
        return h('span', { class: 'd-inline-flex align-items-center' }, [
          content,
          props.variant === 'spinner' &&
            props.message &&
            h('span', { class: `ms-2 text-${props.color}` }, props.message),
        ]);
      }

      return h(
        'div',
        {
          class: props.variant === 'spinner' ? 'text-center' : '',
        },
        [content]
      );
    };
  },
});
